import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';

// PrimeNG Imports
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CompaniesService } from 'src/app/services/companies.service';
import { ProgressBarModule } from 'primeng/progressbar';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { environment } from "../../../environments/environment";
import { OnboardingTrackingService } from 'src/app/services/onboarding-tracking.service';
import { CsrfService } from 'src/app/services/csrf.service';
import { FunnelAnalyticsService } from 'src/app/services/funnel-analytics.service';

@Component({
  selector: 'app-complete-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
    ProgressBarModule
  ],
  providers: [MessageService],
  templateUrl: './complete-profile.component.html',
  styleUrl: './complete-profile.component.scss'
})
export class CompleteProfileComponent implements OnInit {
  private _baseUrl = `${environment.baseUrl}`;

  // Simple workspace name form
  workspaceForm!: FormGroup;

  loading: boolean = false;
  isComplete = false;
  selectedType: 'company' | 'agency' = 'company';

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private companiesService: CompaniesService,
    private amplifyService: AuthenticatorService,
    private http: HttpClient,
    private trackingService: OnboardingTrackingService,
    private csrfService: CsrfService,
    private funnel: FunnelAnalyticsService
  ) {}

  async ngOnInit() {
    // Track profile started
    this.trackingService.trackProfileStarted();

    // Initialize simple form
    this.workspaceForm = this.formBuilder.group({
      workspaceName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]]
    });

    // Check if user already has a company/profile completed
    const session = await fetchAuthSession({ forceRefresh: true });
    if (session.tokens?.idToken?.payload['custom:profileCompleted'] == 'true') {
      // Navigate to root - OnboardingGuard will redirect to appropriate page
      this.router.navigate(['/']);
      return;
    }

    // Try to prefill with user's name or email domain
    await this.suggestWorkspaceName();
  }

  /**
   * Suggest a workspace name based on user's email domain
   */
  private async suggestWorkspaceName(): Promise<void> {
    try {
      const userAttributes = await fetchUserAttributes();
      const email = userAttributes.email;

      if (email) {
        // Extract domain and suggest as workspace name
        const domain = email.split('@')[1];
        if (domain && !domain.includes('gmail') && !domain.includes('yahoo') && !domain.includes('hotmail') && !domain.includes('outlook')) {
          // Use domain name as suggestion (e.g., "acme.com" -> "Acme")
          const suggestion = domain.split('.')[0];
          const capitalizedSuggestion = suggestion.charAt(0).toUpperCase() + suggestion.slice(1);
          this.workspaceForm.patchValue({ workspaceName: capitalizedSuggestion });
        }
      }
    } catch (error) {
      console.error('Error suggesting workspace name:', error);
    }
  }

  /**
   * Check if form field is invalid
   */
  selectType(type: 'company' | 'agency'): void {
    this.selectedType = type;
  }

  isFormControlInvalid(controlName: string): boolean {
    const control = this.workspaceForm.get(controlName);
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  /**
   * Submit the simplified profile
   */
  async submitProfile() {
    if (!this.workspaceForm.valid) {
      this.workspaceForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please enter a workspace name.'
      });
      return;
    }

    this.loading = true;
    // Only a failure of the POST itself is a workspace-creation failure; the
    // session refresh / company reload afterwards must not be recorded as one.
    let posted = false;
    try {
      const workspaceName = this.workspaceForm.get('workspaceName')?.value.trim();

      // Get current authenticated user
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const userAttributes = await fetchUserAttributes();

      // Get CSRF token
      let csrfToken = this.csrfService.getCurrentToken();
      if (!csrfToken) {
        csrfToken = await this.csrfService.fetchToken();
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        'X-CSRF-Token': csrfToken || ''
      });

      // Create minimal onboarding data with defaults for required fields
      const onboardingData = {
        organizationType: this.selectedType,
        companyName: workspaceName,
        industry: 'OTHER',
        website: '',
        address: 'TBD',
        city: 'TBD',
        state: 'TBD',
        postalCode: '00000',
        country: 'US',
        email: userAttributes.email || ''
      };

      // Send to onboarding endpoint
      const result = await firstValueFrom(
        this.http.post<any>(`${environment.baseUrl}/onboarding`, onboardingData, { headers })
      );
      posted = true;

      // Track profile completion
      this.trackingService.trackProfileCompleted(this.selectedType);

      // Force refresh the auth session to get updated Cognito attributes
      await fetchAuthSession({ forceRefresh: true });

      // Reload companies to fetch the newly created company
      await this.companiesService.refreshCompanyData();

      if (result.linkedViaInvitation) {
        this.messageService.add({
          severity: 'success',
          summary: 'Welcome!',
          detail: 'You\'ve been linked to an existing workspace.'
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: 'Workspace Created!',
          detail: 'Now let\'s choose your plan to get started.'
        });
      }

      this.isComplete = true;

      // Redirect to root after a short delay - OnboardingGuard will redirect appropriately
      setTimeout(() => {
        this.router.navigate(['/']);
      }, 2000);

    } catch (error) {
      console.error('Error submitting profile:', error);
      if (!posted) {
        this.funnel.trackFailure('workspace', 'workspace_create_failed', error, { company_type: this.selectedType });
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'There was an error creating your workspace. Please try again.'
      });
    } finally {
      this.loading = false;
    }
  }

  async logout(event: Event) {
    event.preventDefault();

    try {
      this.amplifyService.signOut();
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  }
}
